/* Hospital at Home — DOM Translator
 *
 * Walks the DOM and translates text nodes / select attributes whose
 * trimmed content matches a key in the dictionary. Tracks originals
 * so we can fully restore when the locale switches back to English.
 *
 * Trade-off: requires exact string matches. Dynamic strings (with
 * interpolated numbers/dates) won't translate via this path; explicit
 * t() calls handle those.
 */

const ATTRS = ["placeholder", "title", "aria-label", "alt"];
const ORIG_PREFIX = "__hahOrig_";

function shouldSkip(node) {
  let p = node.parentElement;
  while (p) {
    const tag = p.tagName;
    if (tag === "SCRIPT" || tag === "STYLE" || tag === "NOSCRIPT" ||
        tag === "CODE" || tag === "PRE" || tag === "TEXTAREA") return true;
    if (p.hasAttribute && p.hasAttribute("data-no-translate")) return true;
    p = p.parentElement;
  }
  return false;
}

function translateTextNode(node, dict) {
  if (!node || node.nodeType !== Node.TEXT_NODE) return;
  if (shouldSkip(node)) return;
  const original = node.nodeValue;
  if (!original) return;
  const trimmed = original.trim();
  if (!trimmed) return;
  const ar = dict[trimmed];
  if (!ar) return;
  if (node.__hahOriginal === original && node.__hahTranslated) return;
  node.__hahOriginal = original;
  node.nodeValue = original.replace(trimmed, ar);
  node.__hahTranslated = true;
}

function restoreTextNode(node) {
  if (!node || node.nodeType !== Node.TEXT_NODE) return;
  if (node.__hahTranslated && node.__hahOriginal !== undefined) {
    node.nodeValue = node.__hahOriginal;
    node.__hahTranslated = false;
  }
}

function translateAttrs(el, dict) {
  if (!el || el.nodeType !== 1) return;
  if (el.hasAttribute("data-no-translate")) return;
  for (const a of ATTRS) {
    if (!el.hasAttribute(a)) continue;
    const v = el.getAttribute(a);
    const trimmed = (v || "").trim();
    if (!trimmed) continue;
    const ar = dict[trimmed];
    if (!ar) continue;
    const origAttr = ORIG_PREFIX + a;
    if (el[origAttr] === v) continue;
    el[origAttr] = v;
    el.setAttribute(a, v.replace(trimmed, ar));
  }
}

function restoreAttrs(el) {
  if (!el || el.nodeType !== 1) return;
  for (const a of ATTRS) {
    const origAttr = ORIG_PREFIX + a;
    if (el[origAttr] !== undefined) {
      el.setAttribute(a, el[origAttr]);
      delete el[origAttr];
    }
  }
}

function walkSubtree(root, fnText, fnEl) {
  if (!root) return;
  // Element
  if (root.nodeType === 1) fnEl?.(root);
  // Text nodes
  const walker = document.createTreeWalker(
    root,
    NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
    null,
  );
  let n = walker.nextNode();
  while (n) {
    if (n.nodeType === Node.TEXT_NODE) fnText?.(n);
    else if (n.nodeType === 1) fnEl?.(n);
    n = walker.nextNode();
  }
}

let currentObserver = null;
let currentMode = "en";
let pending = false;

function schedule(fn) {
  if (pending) return;
  pending = true;
  requestAnimationFrame(() => {
    pending = false;
    fn();
  });
}

export function startTranslator(dict) {
  stopTranslator();
  currentMode = "ar";
  walkSubtree(document.body,
    (tn) => translateTextNode(tn, dict),
    (el) => translateAttrs(el, dict),
  );
  currentObserver = new MutationObserver((mutations) => {
    schedule(() => {
      if (currentMode !== "ar") return;
      for (const m of mutations) {
        if (m.type === "childList") {
          m.addedNodes.forEach((n) => {
            if (n.nodeType === Node.TEXT_NODE) translateTextNode(n, dict);
            else if (n.nodeType === 1) {
              walkSubtree(n,
                (tn) => translateTextNode(tn, dict),
                (el) => translateAttrs(el, dict),
              );
            }
          });
        } else if (m.type === "characterData") {
          translateTextNode(m.target, dict);
        } else if (m.type === "attributes") {
          translateAttrs(m.target, dict);
        }
      }
    });
  });
  currentObserver.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: ATTRS,
  });
}

export function stopTranslator() {
  if (currentObserver) {
    currentObserver.disconnect();
    currentObserver = null;
  }
  if (currentMode === "ar") {
    walkSubtree(document.body, restoreTextNode, restoreAttrs);
  }
  currentMode = "en";
}
